import { ADMIN_URL } from 'features/admin/paths'

// Segment under `/admin`: the page renders at `/admin/itemeyes/…`, listed in
// the `AdminShell` sidebar (`features/admin/Container.tsx`) but registered in
// `features/admin/routes.ts` as a SIBLING of the shell, so it opens without it.
export const ITEMEYES_URL = 'itemeyes'

// The open screen is the path: `/admin/itemeyes/<section>/<tab>`, and a third
// segment opens one item on the screens that have them: a schema on
// `/admin/itemeyes/naming/schemas/<schemaId>`, a job's results on
// `/admin/itemeyes/scrapers/product-match/<jobId>`, a category scrape's
// results on `/admin/itemeyes/scrapers/category/<scrapeId>`. The tab and item segments
// are optional in the route so a URL that stops short (or names something
// unknown) still matches and is redirected to a real screen rather than
// falling through to the app's not-found page.
export const ITEMEYES_SECTION_PARAM = 'section'
export const ITEMEYES_TAB_PARAM = 'tab'
export const ITEMEYES_ITEM_PARAM = 'itemId'
export const ITEMEYES_SCREEN_SEGMENT = `:${ITEMEYES_SECTION_PARAM}/:${ITEMEYES_TAB_PARAM}?/:${ITEMEYES_ITEM_PARAM}?`

/** The section and tab whose screen holds the schema finder and detail. */
export const ITEMEYES_SCHEMAS_SECTION = 'naming'
export const ITEMEYES_SCHEMAS_TAB = 'schemas'

/** The section and tab whose screen holds the product match jobs. */
export const ITEMEYES_PRODUCT_MATCH_SECTION = 'scrapers'
export const ITEMEYES_PRODUCT_MATCH_TAB = 'product-match'

/** The section and tab whose screen holds the category scrapes. */
export const ITEMEYES_CATEGORY_SECTION = 'scrapers'
export const ITEMEYES_CATEGORY_TAB = 'category'

/** The screens whose path may carry an item segment. */
export const ITEMEYES_ITEM_SCREENS: readonly {
  section: string
  tab: string
}[] = [
  { section: ITEMEYES_SCHEMAS_SECTION, tab: ITEMEYES_SCHEMAS_TAB },
  { section: ITEMEYES_PRODUCT_MATCH_SECTION, tab: ITEMEYES_PRODUCT_MATCH_TAB },
  { section: ITEMEYES_CATEGORY_SECTION, tab: ITEMEYES_CATEGORY_TAB },
]

export const itemEyesScreenPath = (section: string, tab: string): string =>
  `/${ADMIN_URL}/${ITEMEYES_URL}/${section}/${tab}`

const itemEyesItemPath = (
  section: string,
  tab: string,
  itemId: string,
): string => `${itemEyesScreenPath(section, tab)}/${encodeURIComponent(itemId)}`

export const itemEyesSchemaPath = (schemaId: string): string =>
  itemEyesItemPath(ITEMEYES_SCHEMAS_SECTION, ITEMEYES_SCHEMAS_TAB, schemaId)

/** The Product Match screen itself: the form and the jobs list. */
export const itemEyesProductMatchPath = (): string =>
  itemEyesScreenPath(ITEMEYES_PRODUCT_MATCH_SECTION, ITEMEYES_PRODUCT_MATCH_TAB)

/** One job's full-page results. */
export const itemEyesProductMatchJobPath = (jobId: string): string =>
  itemEyesItemPath(
    ITEMEYES_PRODUCT_MATCH_SECTION,
    ITEMEYES_PRODUCT_MATCH_TAB,
    jobId,
  )

/** The Category screen itself: the queue form and the scrape history. */
export const itemEyesCategoryPath = (): string =>
  itemEyesScreenPath(ITEMEYES_CATEGORY_SECTION, ITEMEYES_CATEGORY_TAB)

/** One category scrape's full-page results. */
export const itemEyesCategoryScrapePath = (scrapeId: string): string =>
  itemEyesItemPath(ITEMEYES_CATEGORY_SECTION, ITEMEYES_CATEGORY_TAB, scrapeId)
